import { Box, IconButton, Typography, CircularProgress, Button, Snackbar, Alert, Menu, MenuItem, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { hasValidAddress } from '../utils/homeUtils';
import { useState, useEffect } from 'react';
import { useHomeDetailsContext } from '../contexts/HomeDetailsContext';
import { useHomeAttachmentsContext } from '../contexts/HomeAttachmentsContext';
import AddHomeForm from '../components/AddHomeForm';
import UploadStatusOverlay from '../components/UploadStatusOverlay';
import HomeMainImage from '../components/HomeMainImage';
import ImageSlider from '../components/ImageSlider';
import HomeInfo from '../components/HomeInfo';
import HomeChatSpeedDial from '../components/mobile/HomeChatSpeedDial';
import ChatModal from '../components/mobile/ChatModal';
import TasksSpeedDial from '../components/mobile/TasksSpeedDial';
import TasksModal from '../components/mobile/TasksModal';
import GeneratedRoomsModal from '../components/mobile/GeneratedRoomsModal';
import AllImagesModalMobile from '../components/mobile/AllImagesModalMobile';
import CameraCapture from '../components/mobile/CameraCapture';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import RepairCostEstimationModal from '../components/mobile/RepairCostEstimationModal';
import BuildIcon from '@mui/icons-material/Build';
import SummarizeIcon from '@mui/icons-material/Summarize';
import { useHomeRepairsContext } from '../contexts/HomeRepairsContext';
import { IntelligenceSpeedDial, useHomeIntelligenceOptional } from '../components/intelligence';
import { usePropertyReport } from '../hooks/usePropertyReport';

interface HomeDetailsMobileProps {
  isDetailView: boolean;
}

function HomeDetailsMobile({ isDetailView }: HomeDetailsMobileProps) {
  const navigate = useNavigate();
  const { homeData, loading, isSubmitting, editHome, homeId } = useHomeDetailsContext();

  // Check if intelligence is available AND home has valid address
  const intelligenceContext = useHomeIntelligenceOptional();
  const hasAddress = hasValidAddress(homeData?.details);
  const canShowIntelligence = intelligenceContext && hasAddress;
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [fileCount, setFileCount] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });
  const [photoMenuAnchor, setPhotoMenuAnchor] = useState<null | HTMLElement>(null);

  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [tasksModalOpen, setTasksModalOpen] = useState(false);
  const [roomsModalOpen, setRoomsModalOpen] = useState(false);
  const [allImagesModalOpen, setAllImagesModalOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [repairCostModalOpen, setRepairCostModalOpen] = useState(false);

  // Add repairs context to get repair count
  const { repairs } = useHomeRepairsContext();

  // Calculate unresolved repairs count for badge
  const unresolvedRepairsCount = repairs.filter(repair => !repair.is_resolved).length;

  // Property report generation hook
  const { generateReport, isGenerating: isGeneratingReport, error: reportError, clearError: clearReportError } = usePropertyReport();

  // Handle report generation
  const handleGenerateReport = async () => {
    if (!homeId) return;
    clearReportError();
    await generateReport(homeId);
  };

  // Show error snackbar when report generation fails
  useEffect(() => {
    if (reportError) {
      setSnackbar({
        open: true,
        message: reportError,
        severity: 'error'
      });
      clearReportError();
    }
  }, [reportError, clearReportError]);

  const handleOpenChatModal = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setChatModalOpen(true);
  };

  const handleCloseChatModal = () => {
    setChatModalOpen(false);
  };

  const handleOpenTasksModal = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setTasksModalOpen(true);
  };

  const handleCloseTasksModal = () => {
    setTasksModalOpen(false);
  };

  const handleOpenRoomsModal = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setRoomsModalOpen(true);
  };

  const handleCloseRoomsModal = () => {
    setRoomsModalOpen(false);
  };

  const handleOpenAllImagesModal = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setAllImagesModalOpen(true);
  };

  const handleCloseAllImagesModal = () => {
    setAllImagesModalOpen(false);
  };

  const handleOpenCamera = () => {
    setCameraOpen(true);
  };

  const handleCloseCamera = () => {
    setCameraOpen(false);
  };

  const handleOpenRepairCostModal = () => {
    // Remove focus from any currently focused element before opening modal
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setRepairCostModalOpen(true);
  };

  const handleCloseRepairCostModal = () => {
    setRepairCostModalOpen(false);
    // Optional: restore focus to the button that opened the modal
    setTimeout(() => {
      const repairButton = document.querySelector('[aria-label*="repair"], button[color="warning"]') as HTMLElement;
      if (repairButton) {
        repairButton.focus();
      }
    }, 100);
  };

  // Use the home attachments context
  const { 
    uploadFiles,
    isUploading, 
    isProcessing,
    uploadProgress,
  } = useHomeAttachmentsContext();

  const handleBack = () => {
    navigate('/homes');
  };

  // Wrapper for uploadFiles that tracks file count
  const handleUploadFiles = (files: FileList | null) => {
    if (files) {
      setFileCount(files.length);
      return uploadFiles(files);
    }
    return false;
  };

  const handleEditOpen = () => {
    setEditModalOpen(true);
  };

  const handleEditClose = () => {
    setEditModalOpen(false);
  };

  const handleEditSubmit = async (homeName: string, address: string, details: {
    bedrooms: number;
    bathrooms: number;
    sqft: number;
    price: number;
  }) => {
    // Pass the parameters directly to editHome
    const result = await editHome(homeName, address, details);
    
    if (result.success) {
      handleEditClose();
      setSnackbar({
        open: true,
        message: 'Home updated successfully',
        severity: 'success'
      });
    } else {
      setSnackbar({
        open: true,
        message: result.error || 'Failed to update home',
        severity: 'error'
      });
    }
  };

  const handleCancelUpload = () => {
    // If you need to implement upload cancellation, do it here
    console.log('Upload cancellation not implemented');
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const parseHomeDetails = () => {
    let address = '';
    let bedrooms = 3;
    let bathrooms = 2;
    let sqft = 1800;
    let price = 350000;
    
    if (homeData?.details) {
      try {
        // Check if it's already a JSON object
        const details = typeof homeData.details === 'string' 
          ? JSON.parse(homeData.details) 
          : homeData.details;
        
        address = details.address || '';
        bedrooms = details.bedrooms || 3;
        bathrooms = details.bathrooms || 2;
        sqft = details.sqft || 1800;
        price = details.price || 350000;
      } catch (e) {
        // If parsing fails, assume the entire details is just the address
        console.error('Error parsing home details:', e);
        address = homeData.details;
      }
    }
    
    return { address, bedrooms, bathrooms, sqft, price }; // Return price
  };

  // Add handler for captured images
  const handleCapturedImages = async (files: File[]) => {
    if (files.length > 0) {
      setFileCount(files.length);
      return uploadFiles(files);
    }
    return false;
  };

  return (
    <Box sx={{ 
      height: 'calc(100vh - 64px)',
      display: 'flex',
      flexDirection: 'column',
      p: 2,
      width: '100%',
      overflow: 'hidden'
    }}>
      {isDetailView && (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mb: 2,
          ml: 0.5,
          position: 'relative', // Add position relative
          zIndex: 2002 // Set z-index higher than the drawer (2001)
        }}>
          <IconButton 
            onClick={handleBack}
            color="primary"
            sx={{ 
              mr: 1,
              bgcolor: 'rgba(25, 118, 210, 0.08)',
              width: 36,
              height: 36,
              borderRadius: '50%',
              padding: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              '&:hover': {
                bgcolor: 'rgba(25, 118, 210, 0.15)'
              },
              zIndex: 2002, // Also add z-index here to ensure it's clickable
              position: 'relative' // Ensure position is set for z-index to work
            }}
            aria-label="back to homes"
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography 
            variant="h5" 
            component="h1" 
            sx={{ 
              fontWeight: 500,
              color: 'primary.dark',
              fontFamily: '"Roboto Slab", "Roboto", "Helvetica", "Arial", sans-serif',
              textAlign: 'left'
            }}
          >
            {loading ? 'Loading...' : homeData?.chat_name || 'Home Details'}
          </Typography>
        </Box>
      )}
      
      {/* Add buttons for Upload Photos and Edit Home */}
      {isDetailView && !loading && (
        <Box sx={{ 
          mb: 2,
          display: 'flex', 
          justifyContent: 'space-between',
          gap: 2 
        }}>
          {/* Photo actions button - opens menu with options */}
          <Box>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={(isUploading || isProcessing) ? <CircularProgress size={16} /> : <CloudUploadIcon />}
              onClick={(e) => setPhotoMenuAnchor(e.currentTarget)}
              disabled={isUploading || isProcessing}
              sx={{
                borderRadius: '8px',
                fontWeight: 500,
                boxShadow: 'none',
                textTransform: 'none',
                px: 2,
                py: 0.5
              }}
              size="small"
            >
              Add Photos
            </Button>
            <Menu
              anchorEl={photoMenuAnchor}
              open={Boolean(photoMenuAnchor)}
              onClose={() => setPhotoMenuAnchor(null)}
            >
              <MenuItem onClick={() => {
                document.getElementById('mobile-photo-upload-input')?.click();
                setPhotoMenuAnchor(null);
              }}>
                <CloudUploadIcon fontSize="small" sx={{ mr: 1 }} />
                Upload from device
              </MenuItem>
              <MenuItem onClick={() => {
                handleOpenCamera();
                setPhotoMenuAnchor(null);
              }}>
                <CameraAltIcon fontSize="small" sx={{ mr: 1 }} />
                Take photos
              </MenuItem>
            </Menu>
          </Box>
          
          <Button
            variant="outlined"
            color="primary"
            startIcon={<EditIcon />}
            onClick={handleEditOpen}
            sx={{
              borderRadius: '8px',
              fontWeight: 500,
              boxShadow: 'none',
              textTransform: 'none',
              px: 2,
              py: 0.5
            }}
            size="small"
          >
            Edit Home
          </Button>
          <input
            id="mobile-photo-upload-input"
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp,image/avif"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => {
              handleUploadFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </Box>
      )}
      
      {isDetailView && loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Main image */}
          <HomeMainImage height="220px" />
          
          {/* Buttons row - Updated with 3 buttons */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            mb: 0.5,
            mt: 1, 
            width: '100%',
            gap: 1
          }}>
            <Button 
              variant="outlined"
              color="secondary"
              sx={{
                borderRadius: '8px',
                fontWeight: 500,
                textTransform: 'none',
                py: 0.5,
                px: 1.5,
                flex: 1
              }}
              onClick={handleOpenRoomsModal}
            >
              Generated Rooms
            </Button>
            
            {/* Repair Cost Button - Icon only with badge */}
            <Button
              variant="outlined"
              color="warning"
              sx={{
                borderRadius: '8px',
                fontWeight: 500,
                textTransform: 'none',
                py: 0.5,
                px: 1,
                minWidth: 48, // Set minimum width for icon button
                position: 'relative',
                flex: 0
              }}
              onClick={handleOpenRepairCostModal}
            >
              <BuildIcon />
              {unresolvedRepairsCount > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    backgroundColor: 'error.main',
                    color: 'white',
                    borderRadius: '50%',
                    width: 20,
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 'bold'
                  }}
                >
                  {unresolvedRepairsCount > 99 ? '99+' : unresolvedRepairsCount}
                </Box>
              )}
            </Button>

            {/* Property Report Button - Icon only */}
            <Tooltip title="Generate Property Report" arrow placement="top">
              <span> {/* Wrapper span needed for tooltip on disabled button */}
                <Button
                  variant="outlined"
                  color="success"
                  disabled={isGeneratingReport}
                  sx={{
                    borderRadius: '8px',
                    fontWeight: 500,
                    textTransform: 'none',
                    py: 0.5,
                    px: 1,
                    minWidth: 48,
                    flex: 0
                  }}
                  onClick={handleGenerateReport}
                  data-testid="generate-report-button-mobile"
                >
                  {isGeneratingReport ? <CircularProgress size={20} color="inherit" /> : <SummarizeIcon />}
                </Button>
              </span>
            </Tooltip>

            <Button
              variant="outlined"
              color="primary"
              sx={{
                borderRadius: '8px',
                fontWeight: 500,
                textTransform: 'none',
                py: 0.5,
                px: 1.5,
                flex: 1
              }}
              onClick={handleOpenAllImagesModal}
            >
              All Images
            </Button>
          </Box>
          
          {/* Image Slider */}
          <Box sx={{ height: '120px', mb: 1 }}>
            <ImageSlider height={80} />
          </Box>
          
          {/* HomeInfo component */}
          <Box sx={{ mt: 2, px: 0.5 }}>
            <HomeInfo 
              name={homeData?.chat_name || 'Home'}
              details={homeData?.details || ''}
              compact={true}
            />
          </Box>
        </Box>
      )}

      {/* Add Home Edit Form */}
      <AddHomeForm
        open={editModalOpen}
        onClose={handleEditClose}
        onSubmit={handleEditSubmit}
        isSubmitting={isSubmitting}
        isEdit={true}
        initialValues={{
          name: homeData?.chat_name || '',
          ...parseHomeDetails()
        }}
        title="Edit Home"
        submitButtonText="Update"
      />

      {/* Upload Status Overlay */}
      <UploadStatusOverlay
        isUploading={isUploading}
        isProcessing={isProcessing}
        progress={uploadProgress}
        fileCount={fileCount}
        onCancel={handleCancelUpload}
      />

      {/* Add Snackbar component */}
      <Snackbar 
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Tasks Modal */}
      <TasksModal 
        open={tasksModalOpen}
        onClose={handleCloseTasksModal}
        homeId={homeId || ''}
      />

      {/* Chat Modal */}
      <ChatModal 
        open={chatModalOpen}
        onClose={handleCloseChatModal}
      />

      {/* Generated Rooms Modal */}
      <GeneratedRoomsModal
        open={roomsModalOpen}
        onClose={handleCloseRoomsModal}
        onImageSelect={(imageId) => {
          console.log(`Selected image: ${imageId}`);
          // Handle image selection here
          handleCloseRoomsModal();
        }}
      />

      {/* All Images Modal */}
      <AllImagesModalMobile
        open={allImagesModalOpen}
        onClose={handleCloseAllImagesModal}
      />

      {/* Camera component */}
      <CameraCapture
        open={cameraOpen}
        onClose={handleCloseCamera}
        onCapture={handleCapturedImages}
        isUploading={isUploading}
      />

      {/* Speed Dials */}
      {isDetailView && !loading && (
        <>
          <TasksSpeedDial onClick={handleOpenTasksModal} />
          <HomeChatSpeedDial onClick={handleOpenChatModal} />
          {canShowIntelligence && (
            <IntelligenceSpeedDial
              onClick={() => navigate(`/home/${homeId}/intelligence`)}
            />
          )}
        </>
      )}

      {/* Repair Cost Estimation Modal */}
      <RepairCostEstimationModal
        open={repairCostModalOpen}
        onClose={handleCloseRepairCostModal}
      />
    </Box>
  );
}

export default HomeDetailsMobile;